import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_BADGE_LISTING_FIELDS } from '../../config/r2rListingBadges';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  avatarImageFieldVariants,
  listingCardAuthorUserFields,
  listingCardSponsorHighlightFields,
} from '../../util/avatarImageQueryFields';
import { storableError } from '../../util/errors';
import { LISTING_CARD_IMAGE_LIMIT, LISTING_CARD_MEDIA_FIELDS } from '../../util/listingMedia';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { getWatchedListingIds, MAX_WATCHED_LISTINGS } from '../../util/watchedListings';

// ================ Async Thunks ================ //

const queryWatchedListingsPayloadCreator = (
  queryParams,
  { extra: sdk, dispatch, rejectWithValue }
) => {
  return sdk.listings
    .query(queryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryWatchedListingsThunk = createAsyncThunk(
  'WatchedListingsPage/queryWatchedListings',
  queryWatchedListingsPayloadCreator
);

export const queryWatchedListings = queryParams => dispatch => {
  return dispatch(queryWatchedListingsThunk(queryParams)).unwrap();
};

// ================ Slice ================ //

const watchedListingsPageSlice = createSlice({
  name: 'WatchedListingsPage',
  initialState: {
    queryInProgress: false,
    queryListingsError: null,
    // True after loadData has finished (even if the watch list was empty).
    listingsAreLoaded: false,
  },
  reducers: {
    clearWatchedListingsPage: state => {
      state.queryInProgress = false;
      state.queryListingsError = null;
      state.listingsAreLoaded = false;
    },
    markWatchedListingsLoaded: state => {
      state.queryInProgress = false;
      state.queryListingsError = null;
      state.listingsAreLoaded = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(queryWatchedListingsThunk.pending, state => {
        state.queryInProgress = true;
        state.queryListingsError = null;
        state.listingsAreLoaded = false;
      })
      .addCase(queryWatchedListingsThunk.fulfilled, state => {
        state.queryInProgress = false;
        state.listingsAreLoaded = true;
      })
      .addCase(queryWatchedListingsThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.listingsAreLoaded = true;
        state.queryListingsError = action.payload;
      });
  },
});

export const {
  clearWatchedListingsPage,
  markWatchedListingsLoaded,
} = watchedListingsPageSlice.actions;
export default watchedListingsPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return dispatch(fetchCurrentUser()).then(() => {
    const currentUser = getState().user.currentUser;
    const watchedListingIds = getWatchedListingIds(currentUser);

    if (watchedListingIds.length === 0) {
      dispatch(markWatchedListingsLoaded());
      return Promise.resolve([]);
    }

    return dispatch(
      queryWatchedListings({
        ids: watchedListingIds,
        perPage: Math.min(watchedListingIds.length, MAX_WATCHED_LISTINGS),
        // Include published zero-stock listings (sold out) and listings without stock.
        minStock: 0,
        stockMode: 'match-undefined',
        include: ['images', 'author', 'author.profileImage', 'currentStock'],
        'fields.listing': [
          ...new Set([
            'title',
            'price',
            'deleted',
            'state',
            'publicData.listingType',
            'publicData.categoryLevel1',
            'publicData.categoryLevel2',
            'publicData.categoryLevel3',
            'publicData.transactionProcessAlias',
            'publicData.unitType',
            'publicData.cardStyle',
            'publicData.pickupEnabled',
            'publicData.shippingEnabled',
            'publicData.priceVariationsEnabled',
            'publicData.priceVariants',
            ...listingCardSponsorHighlightFields,
            ...LISTING_BADGE_LISTING_FIELDS,
            ...LISTING_CARD_MEDIA_FIELDS,
          ]),
        ],
        'fields.user': listingCardAuthorUserFields,
        'fields.image': [...listingCardImageFields(variantPrefix), ...avatarImageFieldVariants],
        ...listingCardImageVariantParams(variantPrefix, aspectRatio),
        'limit.images': LISTING_CARD_IMAGE_LIMIT,
      })
    );
  });
};
